/**
 * api.ts — helpers fetch /ha/form pour les widgets hauser (ha/358).
 *
 * Toutes les surfaces hauser communiquent via la convention AJAX HorApp :
 *   POST /ha/form
 *   body application/x-www-form-urlencoded ou JSON avec `formId` ou `fid`
 *   header X-CSRF-Token + X-Requested-With (sentinel AJAX)
 *
 * Réponse : { success: bool, ... }
 */

function readCsrfToken(): string {
    if (typeof document === "undefined") return "";
    const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
    return meta?.content ?? "";
}

export interface HauserUser {
    readonly id: string;
    readonly name: string;
    readonly firstname: string;
    readonly lastname: string;
    readonly nickname: string;
    readonly email: string;
    readonly picture: string | null;
    /** URL du dashboard résolue par rôle côté serveur (staff→pro→garant). Optionnel. */
    readonly dashboardUrl?: string;
}

export interface HauserConsentEntry {
    readonly granted: boolean;
    readonly version: string;
    readonly at: string;
}

export interface HauserFormResponse<T = unknown> {
    readonly success: boolean;
    readonly todo?: string;
    readonly message?: string;
    readonly data?: T;
}

interface CurrentUserResponse {
    readonly success?: unknown;
    readonly user?: unknown;
}

interface ConsentStatusResponse {
    readonly success: boolean;
    readonly kinds: Record<string, HauserConsentEntry | null>;
}

async function postForm<T>(formId: string, params: Record<string, string>): Promise<T> {
    const body = new URLSearchParams({ formId, ...params });
    const resp = await fetch("/ha/form", {
        method:  "POST",
        headers: {
            "X-CSRF-Token":     readCsrfToken(),
            "X-Requested-With": "XMLHttpRequest",
            "Content-Type":     "application/x-www-form-urlencoded",
            "Accept":           "application/json",
        },
        body,
        credentials: "same-origin",
    });
    return resp.json() as Promise<T>;
}

/**
 * Récupère l'utilisateur courant ou null si anonyme.
 *
 * `<hauser-user-chip>` l'appelle pour hydrater son avatar+nom quand
 * aucun `data-user` n'est posé par le serveur en SSR.
 */
export async function fetchCurrentUser(): Promise<HauserUser | null> {
    try {
        const json = await postForm<CurrentUserResponse>("hauser.user.current", {});
        return json.success === true ? normalizeHauserUser(json.user) : null;
    } catch {
        return null;
    }
}

/**
 * Frontière de normalisation : transforme une réponse réseau/SSR (forme `unknown`,
 * possiblement partielle) en `HauserUser` complet, ou `null` si l'identité n'est pas
 * vérifiable (id absent). Défauts sur tous les champs affichables — le rendu ne reçoit
 * jamais `undefined`. Utilisée par le fetch ET par le parse SSR `data-user` du chip.
 */
export function normalizeHauserUser(value: unknown): HauserUser | null {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return null;
    }
    const candidate = value as Record<string, unknown>;
    const id = typeof candidate.id === "string" ? candidate.id : "";
    if (id === "") {
        return null;
    }
    const text = (field: string): string => {
        const fieldValue = candidate[field];
        return typeof fieldValue === "string" ? fieldValue : "";
    };
    return {
        id,
        name:         text("name"),
        firstname:    text("firstname"),
        lastname:     text("lastname"),
        nickname:     text("nickname"),
        email:        text("email"),
        picture:      typeof candidate.picture === "string" ? candidate.picture : null,
        dashboardUrl: text("dashboardUrl"),
    };
}

/**
 * Récupère le statut des consents demandés pour l'user courant.
 *
 * `<hauser-consent-banner>` l'appelle au boot pour décider si le banner
 * doit s'afficher (kind absent = jamais répondu → afficher).
 */
export async function fetchConsentStatus(kinds: readonly string[]): Promise<Record<string, HauserConsentEntry | null>> {
    try {
        const json = await postForm<ConsentStatusResponse>("hauser.consent.status", {
            kinds: kinds.join(","),
        });
        return json.success ? json.kinds : {};
    } catch {
        return {};
    }
}

/**
 * Enregistre un consent (granted=true/false) pour l'user courant.
 * Anonyme → noop côté serveur, success=false.
 */
export async function recordConsent(kind: string, version: string, granted: boolean): Promise<boolean> {
    try {
        const json = await postForm<HauserFormResponse>("hauser.consent.record", {
            kind,
            version,
            granted: granted ? "true" : "false",
        });
        return json.success === true;
    } catch {
        return false;
    }
}

/**
 * Révoque une session hauser par id HAId (ownership vérifié côté serveur).
 */
export async function revokeSession(sessionId: string): Promise<boolean> {
    try {
        const json = await postForm<HauserFormResponse>("hauser.session.revoke", {
            sessionId,
        });
        return json.success === true;
    } catch {
        return false;
    }
}
